/**
 * USGS Wildfire Prediction Component
 * Enhanced wildfire forecasting using government USGS WFPI WMS data
 * Modern UI with beautiful styling and comprehensive functionality
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, GeoJSON, useMap, useMapEvents, Pane } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
    FaArrowLeft, 
    FaSync, 
    FaFire, 
    FaCalendarAlt, 
    FaMapMarkerAlt,
    FaExclamationTriangle,
    FaTimes,
    FaInfoCircle,
    FaWind
} from 'react-icons/fa';
import { MdLayers } from 'react-icons/md';
import usgsWfpiService from '../../services/usgsWfpiService';
import historicalDataService from '../../services/historicalDataService';
import './USGSWildfirePrediction.css';
import { FaSpinner } from 'react-icons/fa';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Texas center coordinates and bounds
const texasCenter = [31.0, -99.0];
const texasBounds = [
    [25.8371, -106.6456], // Southwest corner
    [36.5007, -93.5083]   // Northeast corner
];

/**
 * Build a "world-with-hole" feature from the Texas boundary GeoJSON
 * Creates a mask that darkens everything outside Texas
 */
function buildOutsideTexasMask(texasGeojson) {
  // Handle both FeatureCollection and single Feature formats
  let features = [];
  if (texasGeojson?.type === 'FeatureCollection' && texasGeojson?.features?.length) {
    features = texasGeojson.features;
  } else if (texasGeojson?.type === 'Feature' && texasGeojson?.geometry) {
    features = [texasGeojson];
  } else {
    return null;
  }

  // A world-size outer ring (lon, lat) that encloses the whole map
  const worldRing = [
    [-179.9999, -89.9999],
    [-179.9999,  89.9999],
    [ 179.9999,  89.9999],
    [ 179.9999, -89.9999],
    [-179.9999, -89.9999],
  ];

  // Collect all Texas rings (holes). Works for Polygon and MultiPolygon.
  const texasHoles = [];

  for (const f of features) {
    const g = f.geometry;
    if (!g) continue;

    if (g.type === 'Polygon') {
      const outer = g.coordinates?.[0];
      if (outer && outer.length >= 4) texasHoles.push(outer);
    } else if (g.type === 'MultiPolygon') {
      for (const poly of g.coordinates || []) {
        const outer = poly?.[0];
        if (outer && outer.length >= 4) texasHoles.push(outer);
      }
    }
  }

  if (texasHoles.length === 0) {
    return null;
  }

  // One big polygon: outer = world, inners = all Texas outers (as holes)
  const maskFeature = {
    type: 'Feature',
    properties: { role: 'outside-texas-mask' },
    geometry: {
      type: 'Polygon',
      coordinates: [worldRing, ...texasHoles],
    },
  };
  
  return maskFeature;
}

/**
 * Outside Texas Mask Component
 * Darkens everything outside Texas boundaries
 */
const OutsideTexasMask = ({ texasGeojson, opacity = 0.65 }) => {
  const maskFeature = React.useMemo(() => {
    return buildOutsideTexasMask(texasGeojson);
  }, [texasGeojson]);
  
  if (!maskFeature) {
    return null;
  }

  return (
    <Pane name="outside-texas-mask" style={{ zIndex: 350, pointerEvents: 'none' }}>
      <GeoJSON
        data={maskFeature}
        interactive={false}
        style={{
          stroke: false,
          fillColor: '#000000',
          fillOpacity: opacity,
        }}
      />
    </Pane>
  );
};

/**
 * WMS Layer Component
 */
const WMSLayer = ({ timeValue, isVisible, currentLayer, onLayerReady }) => {
    const map = useMap();
    const layerRef = useRef(null);

    useEffect(() => {
        if (!timeValue) return;

        // Remove existing layer
        if (layerRef.current) {
            map.removeLayer(layerRef.current);
        }

        // Create new layer
        if (isVisible) {
            layerRef.current = usgsWfpiService.createFireLayer(timeValue, currentLayer);
            layerRef.current.addTo(map);
            
            if (onLayerReady) {
                onLayerReady(layerRef.current);
            }
        }

        // Cleanup
        return () => {
            if (layerRef.current) {
                map.removeLayer(layerRef.current);
            }
        };
    }, [map, timeValue, isVisible, currentLayer, onLayerReady]);

    return null;
};

/**
 * Map Click Handler Component
 */
const MapClickHandler = ({ onMapClick, currentTime }) => {
    useMapEvents({
        click: (e) => {
            console.log('Map clicked at:', e.latlng);
            console.log('Current time:', currentTime);
            console.log('onMapClick function:', !!onMapClick);
            
            if (onMapClick && currentTime) {
                console.log('Calling onMapClick handler');
                onMapClick(e.latlng, currentTime);
            } else {
                console.warn('Missing onMapClick or currentTime');
            }
        }
    });
    return null;
};

/**
 * Texas Bounds Handler Component
 */
const TexasBoundsHandler = ({ texasBoundaryData }) => {
    const map = useMap();

    useEffect(() => {
        if (texasBoundaryData) {
            // Use actual GeoJSON bounds for precise fitting
            const texasLayer = L.geoJSON(texasBoundaryData);
            const bounds = texasLayer.getBounds();
            
            map.fitBounds(bounds, {
                padding: [20, 20],
                maxZoom: 8
            });

            // Allow global panning; do not constrain with max bounds
        } else {
            // Fallback to predefined bounds
            map.fitBounds(texasBounds, {
                padding: [20, 20],
                maxZoom: 8
            });

            // Do not set max bounds on fallback either to allow free world panning
        }
    }, [map, texasBoundaryData]);

    return null;
};

/**
 * Texas GeoJSON Boundary Component
 */
const TexasGeoJSONBoundary = ({ texasBoundaryData }) => {
    if (!texasBoundaryData) return null;

    const boundaryStyle = {
        fillColor: "transparent",
        color: "#1e3c72",
        weight: 3,
        opacity: 0.8,
        fillOpacity: 0,
        dashArray: "8, 4"
    };

    return (
        <GeoJSON
            data={texasBoundaryData}
            style={boundaryStyle}
            interactive={false}  // Make boundary non-interactive to allow map clicks
        />
    );
};

/**
 * Main USGS Wildfire Prediction Component
 */
const USGSWildfirePrediction = () => {
    const navigate = useNavigate();
    const mapRef = useRef(null);
    
    // State management
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [availableTimes, setAvailableTimes] = useState([]);
    const [currentTime, setCurrentTime] = useState(null);
    const [isLayerVisible, setIsLayerVisible] = useState(true);
    const [legendUrl, setLegendUrl] = useState(null);
    const [serviceHealth, setServiceHealth] = useState(null);
    const [clickInfo, setClickInfo] = useState(null);
    const [isLoadingClick, setIsLoadingClick] = useState(false);
    const [texasBoundaryData, setTexasBoundaryData] = useState(null);
    const [currentLayer, setCurrentLayer] = useState('wfpi');
    const [availableLayers, setAvailableLayers] = useState([]);
    const [showHistoricalPanel, setShowHistoricalPanel] = useState(false);
    const [histActiveTab, setHistActiveTab] = useState('deadliest');
    const [histData, setHistData] = useState(null);
    const [histLoading, setHistLoading] = useState(false);
    const [histError, setHistError] = useState(null);
    const [histDateSort, setHistDateSort] = useState('newest'); // 'newest' or 'oldest'
    const [showWfpiHint, setShowWfpiHint] = useState(false);
    const [showWfspHint, setShowWfspHint] = useState(false);
  
  // Long-press hover support for touch devices
  const longPressTimerRef = useRef(null);
  const activeLongPressLayerRef = useRef(null);

  const clearLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    activeLongPressLayerRef.current = null;
  };

  const getHoverHandlers = (layerId) => ({
    onMouseEnter: () => {
      // Normal hover for non-touch pointers
      if (layerId === 'wfpi') setShowWfpiHint(true);
      if (layerId === 'wfsp') setShowWfspHint(true);
    },
    onMouseLeave: () => {
      if (layerId === 'wfpi') setShowWfpiHint(false);
      if (layerId === 'wfsp') setShowWfspHint(false);
    },
    onPointerDown: (e) => {
      if (e.pointerType !== 'touch') return; // Only for touch screens
      clearLongPress();
      activeLongPressLayerRef.current = layerId;
      longPressTimerRef.current = setTimeout(() => {
        if (activeLongPressLayerRef.current === 'wfpi') setShowWfpiHint(true);
        if (activeLongPressLayerRef.current === 'wfsp') setShowWfspHint(true);
      }, 500); // 500ms long-press threshold
    },
    onPointerUp: (e) => {
      if (e.pointerType !== 'touch') return;
      clearLongPress();
      // Hide when finger lifts
      setShowWfpiHint(false);
      setShowWfspHint(false);
    },
    onPointerCancel: (e) => {
      if (e.pointerType !== 'touch') return;
      clearLongPress();
      setShowWfpiHint(false);
      setShowWfspHint(false);
    },
  });

    /**
     * Initialize the component with data
     */
    const initializeComponent = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            console.log('Initializing USGS Fire Prediction component...');
            
            // Load available layers
            const layers = usgsWfpiService.getAvailableLayers();
            setAvailableLayers(layers);
            console.log('Available layers loaded:', layers.map(l => l.name));
            
            // Load Texas boundary GeoJSON, available times, and service health in parallel
            const [boundaryResponse, times, health] = await Promise.all([
                fetch('/default_geojsons/texas.geojson'),
                usgsWfpiService.fetchAvailableTimes(currentLayer),
                usgsWfpiService.checkServiceHealth()
            ]);

            // Process Texas boundary data
            if (boundaryResponse.ok) {
                const boundaryData = await boundaryResponse.json();
                setTexasBoundaryData(boundaryData);
                console.log('Texas boundary GeoJSON loaded successfully');
            } else {
                console.warn('Failed to load Texas boundary GeoJSON');
            }

            setAvailableTimes(times);
            setServiceHealth(health);
            
            // Set default time (first available)
            if (times.length > 0) {
                setCurrentTime(times[0]);
            }

            // Set legend URL for current layer
            setLegendUrl(usgsWfpiService.buildLegendUrl(currentLayer));

            console.log(`USGS Fire Prediction initialized with ${times.length} forecast times for ${currentLayer}`);
        } catch (err) {
            console.error('Failed to initialize USGS Fire Prediction:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentLayer]);

    /**
     * Initialize component
     */
    useEffect(() => {
        initializeComponent();
    }, [initializeComponent]);
    // Historical panel activation (single selection); lazy-load data
    const handleHistoricalToggle = useCallback(async () => {
        // Close any active map layer view and show historical data only
        setIsLayerVisible(false);
        setShowHistoricalPanel(true);
        setClickInfo(null);
        if (!histData && !histLoading) {
            try {
                setHistLoading(true);
                setHistError(null);
                const data = await historicalDataService.loadAllHistoricalData();
                setHistData(data);
            } catch (e) {
                setHistError(e?.message || 'Failed to load historical data');
            } finally {
                setHistLoading(false);
            }
        }
    }, [histData, histLoading]);

    /**
     * Handle time selection change
     */
    const handleTimeChange = useCallback((event) => {
        const newTime = event.target.value;
        setCurrentTime(newTime);
        setClickInfo(null); // Clear previous click info
        console.log('Time changed to:', newTime);
    }, []);

    /**
     * Handle layer toggle
     */
    const handleLayerToggle = useCallback(() => {
        // If historical is active, ignore this toggle and keep map hidden
        if (showHistoricalPanel) {
            setShowHistoricalPanel(false);
            setIsLayerVisible(true);
            return;
        }
        setIsLayerVisible(prev => !prev);
        setClickInfo(null); // Clear click info when toggling
    }, [showHistoricalPanel]);

    /**
     * Handle layer change
     */
    const handleLayerChange = useCallback(async (layerId) => {
        // If historical view is active and user clicks any layer (even the same),
        // just close historical and show the map layer.
        if (layerId === currentLayer) {
            if (showHistoricalPanel) {
                setShowHistoricalPanel(false);
                setIsLayerVisible(true);
            }
            return;
        }

        console.log(`Switching from ${currentLayer} to ${layerId}`);
        // Ensure only one mode is active at a time
        setShowHistoricalPanel(false);
        setLoading(true);
        setClickInfo(null); // Clear previous click info
        
        try {
            // Update service layer
            usgsWfpiService.setCurrentLayer(layerId);
            setCurrentLayer(layerId);
            
            // Fetch times for new layer
            const times = await usgsWfpiService.fetchAvailableTimes(layerId);
            setAvailableTimes(times);
            
            // Set default time
            if (times.length > 0) {
                setCurrentTime(times[0]);
            }
            
            // Update legend
            setLegendUrl(usgsWfpiService.buildLegendUrl(layerId));
            
            console.log(`Switched to ${layerId} layer with ${times.length} times`);
        } catch (err) {
            console.error('Failed to switch layer:', err);
            setError(`Failed to switch to ${layerId} layer: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [currentLayer, showHistoricalPanel]);

    /**
     * Check if coordinates are within Texas bounds using simple bounds check
     */
    const isWithinTexas = (latlng) => {
        // Use simple bounds check for now - more reliable than complex polygon check
        const { lat, lng } = latlng;
        const withinBounds = lat >= texasBounds[0][0] && lat <= texasBounds[1][0] && 
                            lng >= texasBounds[0][1] && lng <= texasBounds[1][1];
        
        console.log('Bounds check details:', {
            clickLat: lat,
            clickLng: lng,
            texasBounds: texasBounds,
            withinBounds: withinBounds
        });
        
        return withinBounds;
    };

    /**
     * Handle map click for feature info
     */
    const handleMapClick = useCallback(async (latlng, timeValue) => {
        console.log('handleMapClick called with:', { latlng, timeValue });
        console.log('mapRef.current:', !!mapRef.current);
        
        if (!mapRef.current) {
            console.warn('No map reference available');
            return;
        }

        // Check if click is within Texas bounds
        console.log('Checking if within Texas bounds...');
        const withinTexas = isWithinTexas(latlng);
        console.log('Within Texas:', withinTexas);
        
        if (!withinTexas) {
            console.log('Click outside Texas bounds');
            setClickInfo({
                success: false,
                error: "Please click within Texas boundaries for wildfire risk data",
                coordinates: latlng,
                time: timeValue,
                outsideTexas: true
            });
            return;
        }

        console.log('Click within Texas, fetching USGS data...');
        setIsLoadingClick(true);
        setClickInfo(null);

        try {
            console.log('Calling USGS service...');
            const result = await usgsWfpiService.fetchFeatureInfo(mapRef.current, latlng, timeValue, currentLayer);
            console.log('USGS result:', result);
            setClickInfo(result);
        } catch (error) {
            console.error('Failed to fetch click info:', error);
            setClickInfo({
                success: false,
                error: error.message,
                coordinates: latlng,
                time: timeValue
            });
        } finally {
            setIsLoadingClick(false);
        }
    }, [texasBoundaryData, currentLayer]);

    /**
     * Handle refresh
     */
    const handleRefresh = useCallback(async () => {
        console.log('Refreshing USGS data and clearing cache...');
        usgsWfpiService.clearCache();
        await initializeComponent();
    }, [initializeComponent]);

    /**
     * Navigate back to main map
     */
    const handleBackToMap = useCallback(() => {
        navigate('/home');
    }, [navigate]);

    /**
     * Format date for display
     */
    const formatDateForDisplay = (isoString) => {
        if (!isoString) return '';
        return isoString.slice(0, 10); // YYYY-MM-DD
    };

    /**
     * Get status color based on service health
     */
    const getStatusColor = (status) => {
        switch (status) {
            case 'online': return '#00ff00';
            case 'degraded': return '#ffff00';
            case 'offline': return '#ff0000';
            default: return '#999999';
        }
    };

    return (
        <div 
            className="usgs-wildfire-prediction"
            style={{
                backgroundImage: `url(${process.env.PUBLIC_URL}/images/wildfire-image-4.png)`
            }}
        >
            {/* Header */}
            <div className="usgs-header">
                <div className="header-content">
                    <div className="header-left">
                        <button 
                            className="back-button"
                            onClick={handleBackToMap}
                            title="Back to Main Map"
                        >
                            <FaArrowLeft /> Back to Main
                        </button>
                        <div className="header-info">
                            <h1><FaFire className="header-icon" /> USGS Wildfire Forecast</h1>
                            <p>Enhanced government wildfire prediction using USGS WFPI data</p>
                        </div>
                    </div>
                    
                    <div className="header-right">
                        {/* <div className="service-status">
                            {serviceHealth && (
                                <div className="status-indicator">
                                    <div 
                                        className="status-dot"
                                        style={{ backgroundColor: getStatusColor(serviceHealth.status) }}
                                    ></div>
                                    <span className="status-text">
                                        Service: {serviceHealth.status}
                                    </span>
                                </div>
                            )}
                        </div> */}
                        
                        <button 
                            className="refresh-button"
                            onClick={handleRefresh}
                            disabled={loading}
                            title="Refresh Data"
                        >
                            <FaSync className={loading ? 'spinning' : ''} /> Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading && (
                <div className="loading-container">
                <FaSpinner className="loading-spinner-icon" />
                <h2 style={{color: "red", fontSize: "2.5rem", fontWeight: "700", margin: "20px 0 10px 0", textShadow: "2px 2px 4px rgba(0, 0, 0, 0.3)"}}>Texas Forestation</h2>
                    <p className="usgs-loading-subtext">Loading wildfire prediction information</p>
                </div>
            )}

            {/* Error State */}
            {error && (
                <div className="error-container">
                    <div className="error-content">
                        <div className="error-icon"><FaExclamationTriangle /></div>
                        <h2>Unable to Load USGS Data</h2>
                        <p>{error}</p>
                        <button onClick={handleRefresh} className="retry-button">
                            <FaSync /> Retry
                        </button>
                    </div>
                </div>
            )}

            {/* Main Content */}
            {!loading && !error && (
                <div className="main-content">
                    {/* Top Controls Bar */}
                    <div className="top-controls-bar">
                        <div className="control-item">
                            <label htmlFor="fireDate" className="control-label">
                                <FaCalendarAlt /> Forecast Date
                            </label>
                            <select 
                                id="fireDate" 
                                className="date-select"
                                value={currentTime || ''}
                                onChange={handleTimeChange}
                            >
                                {availableTimes.map(time => (
                                    <option key={time} value={time}>
                                        {formatDateForDisplay(time)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Layer Selection */}
                        <div className="control-item layer-selection">
                            <label className="control-label">
                                <MdLayers /> Prediction Layer
                            </label>
                            <div className="layer-toggle-group">
                                {availableLayers.map(layer => {
                                    // Map layer IDs to appropriate icons
                                    let LayerIcon = FaFire;
                                    if (layer.id === 'wfpi') LayerIcon = FaFire;
                                    else if (layer.id === 'wfsp') LayerIcon = FaWind;
                                    
                                    return (
                                        <button
                                            key={layer.id}
                                            className={`layer-toggle-button ${(currentLayer === layer.id && !showHistoricalPanel) ? 'active' : ''}`}
                                            onClick={() => handleLayerChange(layer.id)}
                                            {...getHoverHandlers(layer.id)}
                                            title={layer.description}
                                            disabled={loading}
                                        >
                                            <LayerIcon />
                                            <span className="layer-name" style={{color: 'white'}}>{layer.name}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Hide/Show Layer removed per request */}
                        <button
                            className={`toggle-button ${showHistoricalPanel ? 'active' : ''}`}
                            onClick={handleHistoricalToggle}
                            title="Historical Fire Records"
                        >
                            📊 Historical Data
                        </button>
                        {/* About WFPI removed per request */}
                    </div>

                    {/* Map Container */}
                    <div className="map-container">
                        {!showHistoricalPanel ? (
                            <>
                                <MapContainer
                                    center={texasCenter}
                                    zoom={6}
                                    style={{ height: '100%', width: '100%' }}
                                    zoomControl={true}
                                    scrollWheelZoom={true}
                                    doubleClickZoom={true}
                                    dragging={true}
                                    ref={(mapInstance) => {
                                        if (mapInstance) {
                                            mapRef.current = mapInstance;
                                            console.log('🗺️ Map reference set:', !!mapRef.current);
                                        }
                                    }}
                                >
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    {texasBoundaryData && (
                                        <OutsideTexasMask 
                                            texasGeojson={texasBoundaryData} 
                                            opacity={0.65} 
                                        />
                                    )}
                                    <TexasBoundsHandler texasBoundaryData={texasBoundaryData} />
                                    <TexasGeoJSONBoundary texasBoundaryData={texasBoundaryData} />
                                    {currentTime && (
                                        <WMSLayer
                                            timeValue={currentTime}
                                            isVisible={isLayerVisible}
                                            currentLayer={currentLayer}
                                        />
                                    )}
                                    <MapClickHandler
                                        onMapClick={handleMapClick}
                                        currentTime={currentTime}
                                    />
                                </MapContainer>

                                {(clickInfo || isLoadingClick) && (
                                    <div className="click-info-panel">
                                        {isLoadingClick ? (
                                            <div className="click-loading">
                                                <div className="mini-spinner"></div>
                                                <span>Getting wildfire data...</span>
                                            </div>
                                        ) : clickInfo.success ? (
                                            <div className="click-success">
                                                <h4><FaFire /> Wildfire Risk Analysis</h4>
                                                <div className="info-row">
                                                    <span className="info-label" style={{color: "#FFDA03"}}><FaCalendarAlt /> Date:</span>
                                                    <span className="info-value" style={{color: "#00A86B"}}>
                                                        {formatDateForDisplay(clickInfo.time)}
                                                    </span>
                                                </div>
                                                <div className="info-row">
                                                    <span className="info-label" style={{color: "#FFDA03"}}><FaMapMarkerAlt /> Location:</span>
                                                    <span className="info-value" style={{color: "#00A86B"}}>
                                                        {clickInfo.coordinates.lat.toFixed(4)}, {clickInfo.coordinates.lng.toFixed(4)}
                                                    </span>
                                                </div>
                                                <div className="info-row">
                                                    <span className="info-label" style={{color: "#FFDA03"}}><FaInfoCircle /> WFPI Value:</span>
                                                    <span className="info-value" style={{color: "#00A86B"}}>
                                                        {clickInfo.value !== null ? (
                                                            // Show special class labels instead of numeric values for non-burnable areas
                                                            clickInfo.value === 249 ? 'Outside US (249)' :
                                                            clickInfo.value === 250 ? 'Snow/Ice (250)' :
                                                            clickInfo.value === 251 ? 'Agricultural Land (251)' :
                                                            clickInfo.value === 252 ? 'Barren (252)' :
                                                            clickInfo.value === 253 ? 'Marsh/Wetland (253)' :
                                                            clickInfo.value === 254 ? 'Water (254)' :
                                                            clickInfo.value === 255 ? 'No Data (255)' :
                                                            clickInfo.value.toFixed(1)
                                                        ) : 'No data'}
                                                    </span>
                                                </div>
                                                <div className="info-row">
                                                    <span className="info-label" style={{color: "#FFDA03"}}><FaExclamationTriangle /> Risk Level:</span>
                                                    <span 
                                                        className="info-value risk-level"
                                                        style={{ 
                                                            color: clickInfo.interpretation.color,
                                                            fontWeight: 'bold',
                                                            color: "#00A86B"
                                                        }}
                                                    >
                                                        {clickInfo.interpretation.label}
                                                    </span>
                                                </div>
                                                <button 
                                                    className="close-info-button"
                                                    onClick={() => setClickInfo(null)}
                                                    title="Close"
                                                >
                                                    <FaTimes />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className={`click-error ${clickInfo.outsideTexas ? 'texas-boundary-error' : ''}`}>
                                                <h4>{clickInfo.outsideTexas ? <><FaMapMarkerAlt /> Outside Texas</> : <><FaExclamationTriangle /> Error</>}</h4>
                                                <p>{clickInfo.error}</p>
                                                {clickInfo.outsideTexas && (
                                                    <div className="texas-hint">
                                                        <small><FaInfoCircle /> This tool focuses on Texas wildfire data only</small>
                                                    </div>
                                                )}
                                                <button 
                                                    className="close-info-button"
                                                    onClick={() => setClickInfo(null)}
                                                    title="Close"
                                                >
                                                    <FaTimes />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {legendUrl && isLayerVisible && (
                                    <div className="legend-container">
                                        <h3 className="legend-title">
                                            <FaFire /> {availableLayers.find(l => l.id === currentLayer)?.name || 'Fire'} Legend
                                        </h3>
                                        <div className="legend-body">
                                            {currentLayer === 'wfpi' ? (
                                                <div className="legend-custom">
                                                    <div className="legend-section-title">Ranges</div>
                                                    <ul className="legend-list">
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#176f2c'}}></span> 0–10 — <strong>Very Low</strong></li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#1e8f1e'}}></span> 11–20 — <strong>Low</strong></li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#7ecb3b'}}></span> 21–30 — <strong>Low to Moderate</strong></li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#d7e33b'}}></span> 31–40 — <strong>Moderate</strong></li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#ffe14a'}}></span> 41–50 — <strong>Moderate to High</strong></li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#ffb84a'}}></span> 51–60 — <strong>High</strong></li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#ff8a3a'}}></span> 61–70 — <strong>High</strong></li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#e35f2d'}}></span> 71–80 — <strong>Very High</strong></li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#cc2a22'}}></span> 81–90 — <strong>Very High</strong></li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#d90202'}}></span> 91–100 — <strong>Extreme</strong></li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#ff4d4d'}}></span> 101–120 — <strong>Extreme</strong></li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#ff00ff'}}></span> 121–140 — <strong>Extreme+</strong></li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#ff1144'}}></span> 141–247 — <strong>Extreme (highest)</strong></li>
                                                    </ul>
                                                    <div className="legend-section-title">Special classes</div>
                                                    <ul className="legend-list">
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#e6e6e6'}}></span> Outside US – area not modeled</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#ffffff', border:'1px solid #c9c9c9'}}></span> Snow/Ice – snow cover or frozen fuels</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#6b6b6b'}}></span> Ag Land – agricultural land; non‑burnable cover</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#6a7b86'}}></span> Barren – rock, desert, non‑vegetated</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#3a7a71'}}></span> Marsh – wetlands; little fire risk</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#78a8d9'}}></span> Water – lakes, rivers, oceans</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#cfcfcf'}}></span> no data</li>
                                                    </ul>
                                                </div>
                                            ) : currentLayer === 'wfsp' ? (
                                                <div className="legend-explainer">
                                                    <div className="legend-explainer-title">Fire Spread Probability (percent)</div>
                                                    <ul className="legend-list">
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#0b2a6b'}}></span> Dark Blue (0 – 0.5) — Very low chance of a one‑acre fire spreading beyond 500 acres.</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#4da6ff'}}></span> Light Blue (0.5 – 1) — Slightly higher but still very low spread probability.</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#00cfd1'}}></span> Cyan (1 – 2) — Low probability of large spread.</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#2ecc71'}}></span> Green (2 – 5) — Moderate‑low probability of spread.</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#ffd64d'}}></span> Yellow (5 – 10) — Moderate probability of spread.</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#ff8c00'}}></span> Orange (10 – 20) — Moderate‑to‑high probability of spread.</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#ff5a36'}}></span> Red‑Orange (20 – 30) — High probability of spread.</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#d90404'}}></span> Red (≥ 30) — Very high probability a small fire could expand beyond 500 acres.</li>
                                                    </ul>
                                                    <div className="legend-explainer-sub">Special classes</div>
                                                    <ul className="legend-list">
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#e6e6e6'}}></span> Outside US – 249 — area not modeled</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#ffffff', border:'1px solid #c9c9c9'}}></span> Snow/Ice – 250 — snow cover or frozen fuels</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#6b6b6b'}}></span> Ag Land – 251 — agricultural land; non‑burnable cover</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#6a7b86'}}></span> Barren – 252 — rock, desert, non‑vegetated</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#3a7a71'}}></span> Marsh – 253 — wetlands; little fire risk</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#78a8d9'}}></span> Water – 254 — lakes, rivers, oceans</li>
                                                        <li className="legend-item"><span className="legend-swatch" style={{background:'#cfcfcf'}}></span> 255 — no data</li>
                                                    </ul>
                                                </div>
                                            ) : (
                                                <img 
                                                    src={legendUrl} 
                                                    alt="Legend" 
                                                    className="legend-image"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        console.warn('Failed to load legend image');
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="historical-container">
                                <div className="historical-header">
                                    <h4>📊 Historical Fire Data</h4>
                                    <div className="historical-controls">
                                        <label style={{color: '#fff', fontSize: '14px', marginRight: '8px'}}>
                                            <FaCalendarAlt style={{marginRight: '6px'}} />
                                            Sort by Date:
                                        </label>
                                        <select 
                                            className="date-sort-select"
                                            value={histDateSort}
                                            onChange={(e) => setHistDateSort(e.target.value)}
                                            style={{
                                                padding: '6px 12px',
                                                borderRadius: '6px',
                                                border: '1px solid #4a5568',
                                                background: '#2d3748',
                                                color: '#fff',
                                                fontSize: '14px',
                                                cursor: 'pointer',
                                                outline: 'none'
                                            }}
                                        >
                                            <option value="newest">Newest First</option>
                                            <option value="oldest">Oldest First</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="historical-tabs">
                                    {[
                                        { id: 'deadliest', name: 'Deadliest Fires' },
                                        { id: 'destructive', name: 'Most Destructive' },
                                        { id: 'stats', name: 'Statistics' }
                                    ].map(tab => (
                                        <button
                                            key={tab.id}
                                            className={`historical-tab-btn ${histActiveTab === tab.id ? 'active' : ''}`}
                                            onClick={() => setHistActiveTab(tab.id)}
                                        >
                                            {tab.name}
                                        </button>
                                    ))}
                                </div>
                                <div className="historical-content">
                                    {histLoading && (
                                        <div className="click-loading"><div className="mini-spinner"></div><span>Loading historical data...</span></div>
                                    )}
                                    {histError && !histLoading && (
                                        <div className="click-error"><p>{histError}</p></div>
                                    )}
                                    {!histLoading && !histError && histData && histData[histActiveTab] && (() => {
                                        const data = histData[histActiveTab].data;
                                        const headers = data?.headers || [];
                                        let rows = data?.rows || [];
                                        
                                        // Find date column (look for 'Year', 'Date', or similar)
                                        const dateColumnIndex = headers.findIndex(h => 
                                            h.toLowerCase().includes('year') || 
                                            h.toLowerCase().includes('date') ||
                                            h.toLowerCase().includes('started')
                                        );
                                        
                                        // Sort rows by date if date column exists
                                        if (dateColumnIndex !== -1) {
                                            const dateColumnName = headers[dateColumnIndex];
                                            rows = [...rows].sort((a, b) => {
                                                const dateA = a[dateColumnName];
                                                const dateB = b[dateColumnName];
                                                
                                                // Try to parse as dates or years
                                                let valA = dateA;
                                                let valB = dateB;
                                                
                                                // If it's a number (like a year), use it directly
                                                if (!isNaN(dateA) && !isNaN(dateB)) {
                                                    valA = Number(dateA);
                                                    valB = Number(dateB);
                                                } else {
                                                    // Try to parse as date
                                                    valA = new Date(dateA).getTime() || 0;
                                                    valB = new Date(dateB).getTime() || 0;
                                                }
                                                
                                                // Sort based on selected order
                                                return histDateSort === 'newest' ? valB - valA : valA - valB;
                                            });
                                        }
                                        
                                        return (
                                        <div className="historical-table-wrapper">
                                            <table className="historical-table">
                                                <thead>
                                                    <tr>
                                                            {headers.map((h, i) => (
                                                            <th key={i}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                        {rows.slice(0, 500).map((row, rIdx) => (
                                                        <tr key={rIdx}>
                                                                {headers.map((h, cIdx) => (
                                                                <td key={cIdx}>{row[h] ?? '-'}</td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {/* Removed About WFPI panel per request */}
            {showWfpiHint && !showHistoricalPanel && (
                <div className="wfpi-hint wfpi-hint-left">
                    <div className="wfpi-hint-title">Wildfire Potential Index (WFPI)</div>
                    <div className="wfpi-hint-body">
                        <p>Indicates how favorable conditions are for a large wildfire to grow if one starts. Higher values = greater potential.</p>
                    </div>
                </div>
            )}
            {showWfspHint && !showHistoricalPanel && (
                <div className="wfpi-hint">
                    <div className="wfpi-hint-title">Fire Spread Probability (WFSP)</div>
                    <div className="wfpi-hint-body">
                        <p>The WFSP is the probability that an existing one‑acre fire will spread and burn more than 500 acres.</p>
                        <p>Predictions for Spread Probability are based on historic average probabilities adjusted up or down by the WFPI value for that day. Historic averages are specific to each location and day‑in‑year.</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default USGSWildfirePrediction;
